import { Chip } from 'primereact/chip';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import React, { useRef, useState } from 'react';
import { ApprovalSequenceFormFields } from '../../../Interfaces/admin/ApprovalSequence.interface';
import { useFieldArray } from 'react-hook-form';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import AccessSelectorDropdown from './AccessSelectorDropdown';

const SequenceDetails = () => {
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [showAccessSelector, setAccessSelector] = useState(false);
  const toastError = useRef<Toast>(null);
  const { fields, append, replace, update } = useFieldArray<
    ApprovalSequenceFormFields,
    'sequence'
  >({
    name: 'sequence',
    rules: {
      validate: (fieldArrayValues) => {
        let isValid = true;
        fieldArrayValues.forEach((step) => {
          if (!step.members?.length) {
            showToast(
              'error',
              toastError,
              'Approval Sequence Error',
              `Step ${step.step} is missing the members! Please add new members or remove the step.`,
              3000
            );
            isValid = false;
          }
        });

        return isValid;
      },
    },
  });
  const selectedField = fields[selectedStepIndex];

  return (
    <React.Fragment>
      <Splitter className="text-standard blackText">
        <SplitterPanel className="m-2" size={10}>
          <div className="flex align-items-center justify-content-between gap-2">
            <div>Approval Step</div>
            <Tooltip target={`#add-step`} content={'Add new step'} />
            <i
              id="add-step"
              className="pi pi-plus cursor-pointer text-green-500"
              onClick={() => {
                append({ members: [], step: fields.length + 1 });
              }}
            />
          </div>
          <div className="flex flex-column gap-2 mt-3">
            {fields?.map((field, index) => {
              const isSelected = index === selectedStepIndex;
              return (
                <div
                  key={field.id}
                  className={`flex align-items-center justify-content-between p-1 border-round ${
                    isSelected
                      ? 'bluwaiBlue text-white'
                      : 'cursor-pointer hover:bg-blue-100'
                  }`}
                  onClick={() => {
                    setSelectedStepIndex(index);
                  }}
                >
                  <div>{field.step}</div>
                  {fields.length > 1 && (
                    <i
                      className={`pi pi-trash cursor-pointer p-1 border-circle hover:bg-gray-300 hover:text-black-alpha-90`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newFields = fields.reduce((acc, updateField) => {
                          if (field.id === updateField.id) {
                            return acc;
                          }

                          if (updateField.step > field.step) {
                            acc.push({
                              ...updateField,
                              step: updateField.step - 1,
                            });
                          } else {
                            acc.push(updateField);
                          }

                          return acc;
                        }, [] as typeof fields);

                        if (
                          isSelected ||
                          fields.length === selectedStepIndex + 1
                        ) {
                          setSelectedStepIndex(0);
                        }

                        replace(newFields);
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </SplitterPanel>
        <SplitterPanel className="m-2" minSize={60}>
          <div className="flex align-items-center justify-content-between gap-2">
            <div>Members</div>
            {fields.length > 0 && (
              <React.Fragment>
                <Tooltip target={`#add-members`} content={'Add new members'} />
                <i
                  id="add-members"
                  className="pi pi-plus cursor-pointer text-green-500"
                  onClick={() => {
                    setAccessSelector(true);
                  }}
                />
              </React.Fragment>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {fields[selectedStepIndex]?.members.map((member) => {
              return (
                <Chip
                  key={member.id}
                  className="w-fit blackText"
                  label={member.name}
                  removable
                  onRemove={() => {
                    const selectedField = fields[selectedStepIndex];

                    update(selectedStepIndex, {
                      step: selectedField.step,
                      members: selectedField.members.filter(
                        (oldMember) => oldMember.id !== member.id
                      ),
                    });
                  }}
                />
              );
            })}
          </div>
        </SplitterPanel>
      </Splitter>
      <AccessSelectorDropdown
        show={showAccessSelector}
        hide={() => {
          setAccessSelector(false);
        }}
        selectedField={selectedField}
        selectedStepIndex={selectedStepIndex}
        updateStep={(index, details) => {
          update(index, details);
        }}
      />
      <ErrorToast toastRef={toastError} />
    </React.Fragment>
  );
};

export default SequenceDetails;
